import React from "react";
import { connect } from "react-redux";
import { Trans } from "react-i18next";
import { speedList } from "../../../constants/reader/dropdownList";
import StorageUtil from "../../../utils/reader/serviceUtils/storageUtil";
import {
  getPollyVoices,
  getStoredVoiceId,
  getStoredVoiceSpeed,
  resolveVoiceById,
  saveVoiceId,
  toPollyRate,
} from "../../../utils/reader/textToSpeechUtil";
import {
  cancelPollyTts,
  getEpubVisiblePageText,
  hasSpeakableContent,
  isPollyTtsAuthorized,
  isPollyTtsSupported,
  speakWithPolly,
  unlockPollyTts,
  waitForEpubLayoutSettle,
} from "../../../utils/reader/pollyTts";

class TextToSpeech extends React.Component {
  constructor(props) {
    super(props);
    this.speakGeneration = 0;
    this.state = {
      isSupported: isPollyTtsSupported(),
      isAudioOn: false,
      selectedVoiceId: getStoredVoiceId(),
      currentSpeed: getStoredVoiceSpeed(),
    };
  }

  componentDidMount() {
    document.addEventListener("tts-page-changed", this.handlePageChanged);
    document.addEventListener("tts-stop", this.handleTtsStop);
  }

  componentWillUnmount() {
    document.removeEventListener("tts-page-changed", this.handlePageChanged);
    document.removeEventListener("tts-stop", this.handleTtsStop);
    this.speakGeneration += 1;
    cancelPollyTts();
  }

  handlePageChanged = () => {
    if (!this.state.isAudioOn || !this.props.isReading) {
      return;
    }
    cancelPollyTts();
    this.speakCurrentPage();
  };

  handleTtsStop = () => {
    this.speakGeneration += 1;
    cancelPollyTts();
    this.setState({ isAudioOn: false });
  };

  canContinue = () => this.state.isAudioOn && this.props.isReading;

  handleChangeAudio = () => {
    if (this.state.isAudioOn) {
      this.speakGeneration += 1;
      cancelPollyTts();
      this.setState({ isAudioOn: false });
      return;
    }

    if (!isPollyTtsAuthorized()) {
      alert("請先登入後再使用聽書");
      return;
    }

    unlockPollyTts();
    this.setState(
      {
        isAudioOn: true,
        selectedVoiceId: getStoredVoiceId(),
        currentSpeed: getStoredVoiceSpeed(),
      },
      () => {
        this.speakCurrentPage();
      }
    );
  };

  getPageTextFromCfi = async () => {
    const currentLocation =
      this.props.currentEpub.rendition.currentLocation();
    const cfibase = currentLocation.start.cfi
      .replace(/!.*/, "")
      .replace("epubcfi(", "");
    const cfistart = currentLocation.start.cfi
      .replace(/.*!/, "")
      .replace(/\)/, "");
    const cfiend = currentLocation.end.cfi
      .replace(/.*!/, "")
      .replace(/\)/, "");
    const cfiRange = `epubcfi(${cfibase}!,${cfistart},${cfiend})`;
    const range = await this.props.currentEpub.getRange(cfiRange);
    return range.toString();
  };

  getPageText = async () => {
    let text = "";
    if (this.props.currentBook.format === "EPUB") {
      const rendition = this.props.currentEpub?.rendition;
      text = getEpubVisiblePageText(rendition);
      if (!text || !/\S/.test(text)) {
        try {
          text = await this.getPageTextFromCfi();
        } catch (err) {
          console.warn("TTS CFI fallback failed", err);
          text = "";
        }
      }
    } else {
      text = this.props.htmlBook.rendition.visibleText();
    }

    return text
      .replace(/\s\s/g, "")
      .replace(/\r/g, "")
      .replace(/\n/g, "")
      .replace(/\t/g, "")
      .replace(/\f/g, "");
  };

  speakCurrentPage = async (
    voiceId = this.state.selectedVoiceId,
    speed = this.state.currentSpeed
  ) => {
    const voice = resolveVoiceById(voiceId);
    if (!voice) {
      return;
    }

    const generation = ++this.speakGeneration;
    const isCurrent = () =>
      generation === this.speakGeneration && this.canContinue();

    await waitForEpubLayoutSettle();
    if (!isCurrent()) {
      return;
    }

    const text = await this.getPageText();
    if (!isCurrent()) {
      return;
    }

    if (!hasSpeakableContent(text)) {
      this.setState({ isAudioOn: false });
      return;
    }

    try {
      const result = await speakWithPolly({
        text,
        language: voice.language,
        rate: toPollyRate(speed),
        shouldContinue: isCurrent,
      });

      if (!isCurrent()) {
        return;
      }

      if (result === "empty") {
        this.setState({ isAudioOn: false });
        return;
      }

      if (result !== "completed") {
        return;
      }

      if (this.props.currentBook.format === "EPUB") {
        document.dispatchEvent(new CustomEvent("tts-next-page"));
        return;
      }

      this.props.htmlBook.rendition.next();
      this.speakCurrentPage();
    } catch (error) {
      if (generation !== this.speakGeneration) {
        return;
      }
      console.error("TTS error", error);
      this.setState({ isAudioOn: false });
    }
  };

  handleChangeVoiceLang = (event) => {
    const voiceId = event.target.value;
    const voice = resolveVoiceById(voiceId);
    if (!voice) {
      return;
    }

    saveVoiceId(voice.id);
    this.setState({ selectedVoiceId: voice.id }, () => {
      this.speakCurrentPage(voice.id, this.state.currentSpeed);
    });
  };

  handleChangeVoiceSpeed = (event) => {
    const speed = event.target.value;
    StorageUtil.setReaderConfig("voiceSpeed", speed);
    this.setState({ currentSpeed: speed }, () => {
      this.speakCurrentPage(this.state.selectedVoiceId, speed);
    });
  };

  render() {
    if (!this.state.isSupported) {
      return null;
    }

    const { isAudioOn, selectedVoiceId, currentSpeed } = this.state;
    const voices = getPollyVoices();

    return (
      <>
        <div className="single-control-switch-container">
          <span className="single-control-switch-title">
            <Trans>Turn on text-to-speech</Trans>
          </span>
          <span
            className={["single-control-switch", isAudioOn ? "" : "off"].join(
              " "
            )}
            onClick={this.handleChangeAudio}
            style={isAudioOn ? {} : { opacity: 0.6 }}
          >
            <span
              className="single-control-button"
              style={{
                transform: isAudioOn ? "translateX(20px)" : "translateX(0px)",
                transition: "transform 0.5s ease",
              }}
            />
          </span>
        </div>

        {isAudioOn && (
          <>
            <div
              className="setting-dialog-new-title"
              style={{
                marginLeft: "20px",
                width: "88%",
                marginTop: "20px",
                fontWeight: 500,
              }}
            >
              Voice
              <select
                id="text-speech-voice"
                className="lang-setting-dropdown"
                value={selectedVoiceId}
                onChange={this.handleChangeVoiceLang}
              >
                {voices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.label}
                  </option>
                ))}
              </select>
            </div>
            <div
              className="setting-dialog-new-title"
              style={{ marginLeft: "20px", width: "88%", fontWeight: 500 }}
            >
              Speed
              <select
                id="text-speech-speed"
                className="lang-setting-dropdown"
                value={currentSpeed}
                onChange={this.handleChangeVoiceSpeed}
              >
                {speedList.option.map((item) => (
                  <option
                    key={`speed-${item}`}
                    value={item}
                    className="lang-setting-option"
                  >
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currentEpub: state.book.currentEpub,
  currentBook: state.book.currentBook,
  htmlBook: state.reader.htmlBook,
  locations: state.progressPanel.locations,
  isReading: state.book.isReading,
});

export default connect(mapStateToProps, {})(TextToSpeech);
